import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../../configuration";
import { useSignalR } from "../../../../hooks";
import { FetchError, GetAgendaItemResourcesDto, queryKeys, useAccessToken } from "../../../";
import { optimisticallyUpdateQueryData } from "../../utilities";

type Variables = {
  resourceGuid: string;
  agendaItemGuid: string;
};

const useDeleteAgendaItemResourceRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function deleteAgendaItemResourceRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();

    const response = await fetch(`${configuration.apiRootUrl}/agenda-item-resources/${variables.resourceGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "delete",
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { deleteAgendaItemResourceRequest };
};

const useDeleteAgendaItemResource = () => {
  const { deleteAgendaItemResourceRequest } = useDeleteAgendaItemResourceRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: deleteAgendaItemResourceRequest,
    onMutate: async (variables) => {
      let cachedResource = await optimisticallyUpdateQueryData<GetAgendaItemResourcesDto>(
        queryClient,
        queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
        (cachedData) => ({
          resources: cachedData.resources.filter((resource) => resource.guid !== variables.resourceGuid),
        })
      );

      return { cachedResource };
    },
    onSuccess: async (_, variables) => {
      signalR.sendRefetchAgendaItemContent(variables.agendaItemGuid);
    },
    onError: async (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
        context?.cachedResource
      );
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.meetings.getAgendaItemResources(variables.agendaItemGuid),
      });
    },
  });
};

export { useDeleteAgendaItemResource };
